<template>
  <div class="container mt-4 mb-5">
    <div class="d-flex justify-content-between align-items-center mb-3">
      <div>
        <h3 class="m-0">API-Preisübersicht</h3>
        <div class="text-muted small">Schreibgeschützte Hilfeansicht für den aktuellen Firmenplan.</div>
      </div>
      <router-link class="btn btn-outline-secondary btn-sm" :to="{ name: 'UserSettings' }">
        Zurück zu Einstellungen
      </router-link>
    </div>

    <div v-if="loadError" class="alert alert-danger">
      {{ loadError }}
    </div>

    <div v-else-if="!pricingHelp" class="alert alert-secondary">
      Für die aktuelle Firma ist keine API-Preisübersicht verfügbar.
    </div>

    <template v-else>
      <div class="card mb-4">
        <div class="card-body">
          <div class="row g-3">
            <div class="col-md-3">
              <label class="form-label">Plan</label>
              <input class="form-control" :value="pricingHelp.planDisplayName || pricingHelp.planCode" disabled />
            </div>
            <div class="col-md-4">
              <label class="form-label">Abrechnungsmodell</label>
              <input class="form-control" :value="pricingHelp.billingModel" disabled />
            </div>
            <div class="col-md-2">
              <label class="form-label">Credits / Monat</label>
              <input class="form-control" :value="String(pricingHelp.includedCreditsPerMonth)" disabled />
            </div>
            <div class="col-md-3">
              <label class="form-label">Firmen-Cap für Mehrnutzung</label>
              <input class="form-control" :value="formatNullable(pricingHelp.maxOverageCreditsPerMonth)" disabled />
            </div>
          </div>

          <div class="text-muted small mt-3">
            Gezählt werden erfolgreiche 2xx-Antworten im Kalendermonat {{ pricingHelp.timeZoneId }}.
          </div>
        </div>
      </div>

      <div class="card">
        <div class="card-body">
          <h5 class="card-title mb-3">Billable Funktionen</h5>

          <div class="table-responsive">
            <table class="table table-striped align-middle">
              <thead>
                <tr>
                  <th>Funktion</th>
                  <th>Beschreibung</th>
                  <th class="text-end">Credits</th>
                  <th class="text-end">Preis / erfolgreicher Aufruf</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="operation in pricingHelp.operations || []" :key="operation.operationKey">
                  <td>
                    <div class="fw-semibold">{{ operation.displayName }}</div>
                    <div class="text-muted small">{{ operation.operationKey }}</div>
                  </td>
                  <td>{{ operation.description || "-" }}</td>
                  <td class="text-end">{{ operation.includedCreditCost }}</td>
                  <td class="text-end">{{ formatCents(operation.overagePriceCents) }}</td>
                </tr>
                <tr v-if="!pricingHelp.operations || pricingHelp.operations.length === 0">
                  <td colspan="4" class="text-center text-muted py-4">
                    Für diesen Plan sind derzeit keine Operationen hinterlegt.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script setup>
import { onMounted, ref } from "vue";
import ApiPricingHelpService from "@/services/ApiPricingHelpService";

const pricingHelp = ref(null);
const loadError = ref("");

function formatNullable(value) {
  return value === null || value === undefined ? "unbegrenzt" : String(value);
}

function formatCents(value) {
  const cents = Number.isFinite(value) ? value : 0;
  return new Intl.NumberFormat("de-DE", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 2,
  }).format(cents / 100);
}

async function loadPricingHelp() {
  loadError.value = "";
  pricingHelp.value = null;

  try {
    const response = await ApiPricingHelpService.getCurrent();
    pricingHelp.value = response?.data || null;
  } catch (error) {
    loadError.value = "Die API-Preisübersicht konnte nicht geladen werden.";
  }
}

onMounted(() => {
  loadPricingHelp();
});
</script>
